// mapDisplayConfig.ts — EV-356 / EV-360 (Patrick 2026-06-08)
// Shared accessor for the admin-controlled map config (admin.map_display_config,
// edited in the admin "Map Display" config screen). Callers read with a hardcoded
// fallback so the map always works even before the fetch resolves or if the
// endpoint is unreachable.
//
// 🔴 REWRITTEN 2026-08-27 (INF-1677) — this file was the single worst Neon cost
// offender in the entire codebase, and it was not even obvious it was running.
//
// It used to do this at MODULE SCOPE:
//     load();
//     if (typeof window !== 'undefined') setInterval(load, 60000);
// Because that sits at import time rather than inside a component, it started the
// moment ANY consumer page loaded and then polled forever — whether or not a map
// was ever rendered, whether or not the tab was visible, for the entire lifetime of
// the tab. One forgotten tab = a database query every 60 seconds, indefinitely.
//
// Neon bills compute-hours while awake and suspends after 5 minutes idle, so that
// alone made suspension impossible. Measured cost of the consumer polling as a
// whole: ~$30/month with nobody looking at anything.
//
// THREE CHANGES:
//   1. LAZY — nothing starts until a caller actually reads the config.
//   2. VISIBILITY-AWARE — no requests while the tab is hidden.
//   3. 60s → 15 MINUTES. This is CONFIGURATION. It changes when Patrick edits it in
//      admin, not once a minute. 15 min also aligns with the Neon convoy law
//      (~/platform/policy/neon-convoy.md) so it lands with the other quarter-hour
//      traffic rather than waking the compute on its own schedule.
import { visiblePoll } from './visiblePoll';

const REFRESH_MS = 15 * 60 * 1000;

let _cfg: Record<string, any> = {};
let _started = false;

function apiBase(): string {
  const env = (import.meta as any).env?.VITE_API_URL;
  return env ? String(env).replace(/\/$/, '') : '';
}

async function load(): Promise<void> {
  try {
    const r = await fetch(`${apiBase()}/api/map-display-config`);
    const d = await r.json();
    if (d && d.config && typeof d.config === 'object') _cfg = d.config;
  } catch { /* keep last/empty — callers fall back */ }
}

/** Start fetching only once something actually wants the config. Idempotent. */
function ensureStarted(): void {
  if (_started || typeof window === 'undefined') return;
  _started = true;
  visiblePoll(load, REFRESH_MS);
}

/**
 * Explicitly begin refreshing — call from a map component's mount effect if you
 * want the config warm before first read. Optional; readers start it anyway.
 */
export function startMapCfg(): void { ensureStarted(); }

/** Numeric config value with a fallback (handles string-encoded numbers). */
export function mapCfgNum(key: string, fallback: number): number {
  ensureStarted();
  const v = _cfg[key];
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
}

export function getMapCfg(): Record<string, any> {
  ensureStarted();
  return _cfg;
}
